import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import DOMPurify from 'dompurify';
import { Card, Button, Field, Input, SectionTitle } from '../../components/ui';
import { Icon } from '../../components/icons';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../lib/theme';
import {
  emailTemplatesApi,
  uploadAdminAttachment,
  type EmailTemplate,
  type EmailTemplateVariable,
} from '../../lib/api';
import { v1ProfileOptions } from '../../lib/v1';
import { CareNotifyTemplatesRow } from './CareNotifyTemplatesRow';

/**
 * Batch-12b — feste Empfängerkreise für den Default-Empfänger einer Vorlage.
 * Ergänzt werden sie im Select um die Profiltyp-Rollen aus dem Kundenprofil
 * ("Rolle (Profiltyp)", z.B. Rechnungsempfänger) — live aus profile_options.
 */
const EMPFAENGERKREISE: Array<{ value: string; labelKey: string }> = [
  { value: 'kreis:aktive_kunden', labelKey: 'settings.email.empfaengerkreis.aktive_kunden' },
  { value: 'kreis:rechnungsempfaenger', labelKey: 'settings.email.empfaengerkreis.rechnungsempfaenger' },
  { value: 'kreis:interessenten', labelKey: 'settings.email.empfaengerkreis.interessenten' },
  { value: 'kreis:aktive_bks', labelKey: 'settings.email.empfaengerkreis.aktive_bks' },
];

interface EmailTemplatesSettingsProps {
  dark?: boolean;
}

/** Im Editor gehaltener Anhang: nur die ID wird persistiert, `name` ist UI-only. */
interface AttachmentChip {
  id: number;
  name: string;
}

/** Editor-Zustand. `id === null` ⇒ neue, noch nicht gespeicherte Vorlage. */
interface Draft {
  id: number | null;
  name: string;
  default_empfaenger: string;
  subject: string;
  body: string;
  attachments: AttachmentChip[];
}

const EMPTY_DRAFT: Draft = { id: null, name: '', default_empfaenger: '', subject: '', body: '', attachments: [] };

/**
 * Entfernt <script>-Tags (und JS-/data-URI-Quellen) aus dem Vorschau-HTML.
 * Importierte CRM-1.0-Vorlagen enthalten teils eingebettete Tracking-Skripte;
 * im sandboxed <iframe> wirft deren localStorage-Zugriff sonst einen
 * unbehandelten SecurityError in der Konsole. Die Vorschau soll nur HTML/CSS
 * darstellen, kein JS ausführen.
 */
function sanitizePreview(html: string): string {
  return html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<script\b[^>]*\/>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/javascript:/gi, '');
}

/**
 * Rahmen um den TipTap-Editor (ersetzt den vorherigen Plain-Text-Textarea).
 * `.ProseMirror` ist die von TipTap gerenderte contenteditable-Wurzel.
 */
function editorWrapperStyle(t: ReturnType<typeof getThemeColors>): React.CSSProperties {
  return {
    width: '100%',
    minHeight: 220,
    border: `1px solid ${t.border}`,
    borderRadius: 9,
    padding: '10px 12px',
    fontSize: 13,
    lineHeight: 1.55,
    color: t.heading,
    background: t.inputBg,
    fontFamily: 'Outfit, sans-serif',
  };
}

function toolbarButtonStyle(t: ReturnType<typeof getThemeColors>, active: boolean): React.CSSProperties {
  return {
    minWidth: 28,
    padding: '4px 8px',
    borderRadius: 6,
    border: `1px solid ${active ? BRAND : t.border}`,
    background: active ? BRAND_LIGHT : t.chipBg,
    color: active ? BRAND : t.text,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Outfit, sans-serif',
  };
}

export function EmailTemplatesSettings({ dark = false }: EmailTemplatesSettingsProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [variables, setVariables] = useState<EmailTemplateVariable[]>([]);
  // Batch-12b: Profiltyp-Rollen (Label = gespeicherter Wert) für den Default-Empfänger.
  const [profiltypRollen, setProfiltypRollen] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  // Suchfilter für die Vorlagen-Liste (Name + Betreff, clientseitig).
  const [search, setSearch] = useState('');

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Live-Vorschau
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Test-Versand
  const [testTo, setTestTo] = useState('');
  const [sending, setSending] = useState(false);
  const [testMsg, setTestMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const fileRef = useRef<HTMLInputElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);

  // WYSIWYG-Editor für den Nachrichtentext (ersetzt den vorherigen Plain-Text-
  // Textarea). Body bleibt HTML — genau das, was das Backend beim Rendern
  // (mails.v2.template: {!! $bodyHtml !!}) und beim Versand (Brevo/MS Graph:
  // htmlContent) ohnehin erwartet. {{token}}-Platzhalter sind reiner Text
  // für TipTap und werden beim Parsen/Serialisieren nicht angefasst.
  const bodyEditor = useEditor({
    // Vermeidet ein SSR/Hydration-Warning von TipTap v3 in Test-/CRA-Umgebungen
    // ohne echtes SSR — der Editor rendert stattdessen nach dem Mount.
    immediatelyRender: false,
    extensions: [
      // Tiptap v3 bündelt die Link-Extension bereits in StarterKit — eine
      // zusätzliche Link.configure()-Instanz würde zur Laufzeit kollidieren
      // ("Duplicate extension names"), daher hier per StarterKit-Option
      // konfiguriert statt separat registriert.
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: false,
          // Tiptaps Default fügt target="_blank" rel="noopener noreferrer nofollow"
          // an jeden Link an (per Optionen-Merge additiv, ein leeres Objekt
          // überschreibt NICHT einzelne Defaults). Explizit alle drei auf
          // "kein Attribut" setzen, sonst bekommt jeder bereits gespeicherte
          // Link beim nächsten Öffnen im Editor plötzlich neue Attribute —
          // Rundtrip-Treue für bestehende Vorlagen geht vor.
          HTMLAttributes: { target: null, rel: null, class: null },
        },
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-label': tr('settings.email.field.body'),
        'aria-multiline': 'true',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setDraft(prev => (prev ? { ...prev, body: html } : prev));
    },
  });

  // Editor-Inhalt mit dem geladenen Entwurf synchronisieren — beim Öffnen
  // (neu/bearbeiten) und beim Wechsel auf einen anderen Entwurf. `false` als
  // zweites Argument unterdrückt ein erneutes onUpdate (kein Zirkelbezug).
  useEffect(() => {
    if (!bodyEditor) return;
    if (!draft) {
      bodyEditor.commands.clearContent();
      return;
    }
    if (bodyEditor.getHTML() !== (draft.body || '<p></p>')) {
      // Tiptap v3: zweites Argument ist ein Options-Objekt, kein Boolean mehr.
      bodyEditor.commands.setContent(draft.body || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyEditor, draft?.id]);

  // Editor liegt unterhalb der (langen) Vorlagen-Liste — beim Öffnen dorthin
  // scrollen, sonst wirkt "Bearbeiten" bei vielen Vorlagen wie ein No-op.
  function scrollToEditor() {
    window.setTimeout(() => {
      editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  // ── Laden (Liste + Variablen) ──────────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const list = await emailTemplatesApi.list();
      setTemplates(list);
    } catch {
      setListError(tr('settings.email.error.load'));
    } finally {
      setLoading(false);
    }
  }, [tr]);

  useEffect(() => {
    loadTemplates();
    emailTemplatesApi.getVariables().then(setVariables).catch(() => setVariables([]));
    v1ProfileOptions.grouped()
      .then(g => setProfiltypRollen((g['profiltyp'] ?? []).map(o => String(o.value))))
      .catch(() => setProfiltypRollen([]));
  }, [loadTemplates]);

  // ── Live-Vorschau (debounced) ──────────────────────────────────────────
  useEffect(() => {
    if (!draft) return;
    if (!draft.body.trim() && !draft.subject.trim()) {
      setPreviewHtml('');
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const handle = window.setTimeout(async () => {
      try {
        const html = await emailTemplatesApi.preview({ subject: draft.subject, body: draft.body });
        if (!cancelled) setPreviewHtml(html);
      } catch {
        if (!cancelled) setPreviewHtml('');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [draft]);

  // ── Editor öffnen ──────────────────────────────────────────────────────
  function openNew() {
    setFormError(null);
    setSavedNote(false);
    setTestMsg(null);
    setPreviewHtml('');
    setDraft({ ...EMPTY_DRAFT });
    scrollToEditor();
  }

  function openEdit(tpl: EmailTemplate) {
    setFormError(null);
    setSavedNote(false);
    setTestMsg(null);
    setDraft({
      id: tpl.id,
      name: tpl.name ?? '',
      default_empfaenger: tpl.default_empfaenger ?? '',
      subject: tpl.subject ?? '',
      body: tpl.body ?? '',
      attachments: (tpl.attachments ?? []).map(id => ({
        id,
        name: tr('settings.email.attachment_fallback', { id }),
      })),
    });
    scrollToEditor();
  }

  function closeEditor() {
    setDraft(null);
    setPreviewHtml('');
    setTestMsg(null);
  }

  // ── Variable an der Cursor-Position im Editor einfügen ─────────────────
  // insertContent fügt reinen Text ein — {{token}} wird nicht als HTML
  // interpretiert (keine Tags, also nichts zu parsen), bleibt also exakt
  // erhalten. onUpdate() im Editor übernimmt anschließend den neuen HTML-
  // Stand in draft.body.
  function insertVariable(token: string) {
    if (!bodyEditor) return;
    bodyEditor.chain().focus().insertContent(token).run();
  }

  // ── Anhang hochladen ───────────────────────────────────────────────────
  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // erlaubt erneutes Wählen derselben Datei
    if (!file) return;
    setUploading(true);
    setFormError(null);
    try {
      const up = await uploadAdminAttachment(file);
      setDraft(prev =>
        prev ? { ...prev, attachments: [...prev.attachments, { id: up.id, name: up.name }] } : prev,
      );
    } catch {
      setFormError(tr('settings.email.error.upload'));
    } finally {
      setUploading(false);
    }
  }

  function removeAttachment(id: number) {
    setDraft(prev =>
      prev ? { ...prev, attachments: prev.attachments.filter(a => a.id !== id) } : prev,
    );
  }

  // ── Speichern ──────────────────────────────────────────────────────────
  async function save() {
    if (!draft) return;
    if (!draft.name.trim()) {
      setFormError(tr('settings.email.error.name_required'));
      return;
    }
    // Ohne Betreff lehnt Brevo jede Mail mit HTTP 400 ab — das Backend
    // validiert das ebenfalls, hier nur damit der Fehler am Feld erklärt wird.
    if (!(draft.subject ?? '').trim()) {
      setFormError(tr('settings.email.error.subject_required'));
      return;
    }
    setSaving(true);
    setFormError(null);
    setSavedNote(false);
    // Der WYSIWYG-Output wird gespeichert UND unverändert versendet
    // (BrevoMailer/MS-Graph erwarten bereits fertiges HTML) — vor dem
    // Speichern client-seitig sanitizen, unabhängig davon, was TipTaps
    // eigenes Editor-Schema ohnehin schon ausschließt (<script>, on*-Attribute,
    // javascript:-URIs). {{token}}-Platzhalter sind reiner Text und bleiben
    // von DOMPurify unangetastet.
    const input = {
      name: draft.name.trim(),
      subject: draft.subject,
      body: DOMPurify.sanitize(draft.body),
      attachments: draft.attachments.map(a => a.id),
      default_empfaenger: draft.default_empfaenger || null,
    };
    try {
      const saved = draft.id
        ? await emailTemplatesApi.update(draft.id, input)
        : await emailTemplatesApi.create(input);
      setSavedNote(true);
      setDraft(d => (d ? { ...d, id: saved.id } : d));
      await loadTemplates();
    } catch {
      setFormError(tr('settings.email.error.save'));
    } finally {
      setSaving(false);
    }
  }

  // ── Löschen ────────────────────────────────────────────────────────────
  async function remove(tpl: EmailTemplate) {
    if (!window.confirm(tr('settings.email.delete_confirm'))) return;
    try {
      await emailTemplatesApi.remove(tpl.id);
      if (draft?.id === tpl.id) closeEditor();
      await loadTemplates();
    } catch {
      setListError(tr('settings.email.error.delete'));
    }
  }

  // ── Test-Versand ───────────────────────────────────────────────────────
  async function sendTest() {
    if (!draft) return;
    if (!draft.id) {
      setTestMsg({ kind: 'err', text: tr('settings.email.test.save_first') });
      return;
    }
    if (!testTo.trim()) {
      setTestMsg({ kind: 'err', text: tr('settings.email.test.need_recipient') });
      return;
    }
    setSending(true);
    setTestMsg(null);
    try {
      await emailTemplatesApi.send(draft.id, { to: testTo.trim() });
      setTestMsg({ kind: 'ok', text: tr('settings.email.test.success', { to: testTo.trim() }) });
    } catch (err) {
      const resp = (err as { response?: { data?: { message?: string } } }).response;
      const msg = resp?.data?.message;
      const text =
        msg === 'brevo_not_configured'
          ? tr('settings.email.test.not_configured')
          : tr('settings.email.test.error');
      setTestMsg({ kind: 'err', text });
    } finally {
      setSending(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Master-Liste — ausgeblendet, solange der Editor offen ist */}
      {!draft && (
      <Card dark={dark} style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <SectionTitle dark={dark} title={tr('settings.email.title')} subtitle={tr('settings.email.subtitle')} />
          <Button variant="primary" dark={dark} size="sm" icon={<Icon.Plus />} onClick={openNew}>
            {tr('settings.email.new')}
          </Button>
        </div>

        {listError && (
          <div style={{ fontSize: 12, color: '#f04438', marginBottom: 10 }}>{listError}</div>
        )}

        {/* „BK und PB per E-Mail benachrichtigen" (Betreuung erstellen) —
            Vorlage je Empfänger, über der Suche */}
        {!loading && <CareNotifyTemplatesRow dark={dark} templates={templates} />}

        {/* Suchfilter (Name + Betreff) */}
        {!loading && templates.length > 0 && (
          <div style={{ margin: '12px 0 4px', maxWidth: 360 }}>
            <Input
              dark={dark}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={tr('settings.email.search_ph')}
              aria-label={tr('settings.email.search_ph')}
            />
          </div>
        )}

        {(() => {
          const q = search.trim().toLowerCase();
          const filtered = q
            ? templates.filter(tpl =>
                tpl.name.toLowerCase().includes(q) || (tpl.subject ?? '').toLowerCase().includes(q))
            : templates;
          return loading ? (
          <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('settings.email.loading')}</div>
        ) : templates.length === 0 ? (
          <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('settings.email.empty')}</div>
        ) : filtered.length === 0 ? (
          <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('settings.email.no_matches')}</div>
        ) : (
          <div>
            {filtered.map((tpl, i) => (
              <div
                key={tpl.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
                  borderBottom: i < filtered.length - 1 ? `1px solid ${t.borderSoft}` : 'none',
                }}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: 8, background: BRAND_LIGHT,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icon.Mail c={BRAND} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: t.heading }}>{tpl.name}</div>
                  <div style={{
                    fontSize: 12, color: t.muted, marginTop: 2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {tpl.subject || tr('settings.email.no_subject')}
                  </div>
                </div>
                <Button variant="secondary" size="sm" dark={dark} icon={<Icon.Edit />} onClick={() => openEdit(tpl)}>
                  {tr('common.action.edit')}
                </Button>
                <Button variant="danger" size="sm" dark={dark} icon={<Icon.Trash c="#b42318" />} onClick={() => remove(tpl)}>
                  {tr('common.action.delete')}
                </Button>
              </div>
            ))}
          </div>
        );
        })()}
      </Card>
      )}

      {/* Editor + Vorschau — ersetzt die Liste */}
      {draft && (
        <div ref={editorRef} style={{ scrollMarginTop: 16 }}>
        <Card dark={dark} style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <Button
              variant="ghost"
              size="sm"
              dark={dark}
              icon={<Icon.ChevronLR dir="left" />}
              onClick={closeEditor}
            >
              {tr('common.action.back')}
            </Button>
            <SectionTitle
              dark={dark}
              title={draft.id ? tr('settings.email.editor_edit') : tr('settings.email.editor_new')}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 20 }}
               className="email-editor-grid">
            {/* Linke Spalte: Formular */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field label={tr('settings.email.field.name')}>
                <Input
                  value={draft.name}
                  onChange={e => setDraft(d => (d ? { ...d, name: e.target.value } : d))}
                  placeholder={tr('settings.email.field.name_placeholder')}
                  dark={dark}
                />
              </Field>

              <Field label={tr('settings.email.field.subject')}>
                <Input
                  value={draft.subject}
                  onChange={e => setDraft(d => (d ? { ...d, subject: e.target.value } : d))}
                  placeholder={tr('settings.email.field.subject_placeholder')}
                  dark={dark}
                />
              </Field>

              {/* Batch-12b: Default-Empfänger — Rolle aus dem Kundenprofil
                  oder fester Empfängerkreis. */}
              <Field label={tr('settings.email.field.default_empfaenger')} hint={tr('settings.email.field.default_empfaenger_hint')}>
                <select
                  aria-label={tr('settings.email.field.default_empfaenger')}
                  value={draft.default_empfaenger}
                  onChange={e => setDraft(d => (d ? { ...d, default_empfaenger: e.target.value } : d))}
                  style={{
                    width: '100%', boxSizing: 'border-box', fontSize: 13,
                    border: `1px solid ${t.border}`, borderRadius: 8, padding: '9px 10px',
                    background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif',
                  }}
                >
                  <option value="">{tr('settings.email.field.default_empfaenger_none')}</option>
                  <optgroup label={tr('settings.email.field.default_empfaenger_group_profil')}>
                    {profiltypRollen.map(r => (
                      <option key={r} value={`profiltyp:${r}`}>{r}</option>
                    ))}
                  </optgroup>
                  <optgroup label={tr('settings.email.field.default_empfaenger_group_kreis')}>
                    {EMPFAENGERKREISE.map(k => (
                      <option key={k.value} value={k.value}>{tr(k.labelKey)}</option>
                    ))}
                  </optgroup>
                </select>
              </Field>

              <Field label={tr('settings.email.field.body')} hint={tr('settings.email.variables_hint')}>
                {variables.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {variables.map(v => (
                      <button
                        key={v.token}
                        type="button"
                        title={tr('settings.email.insert_variable')}
                        onClick={() => insertVariable(v.token)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '4px 9px', borderRadius: 9999,
                          border: `1px solid ${t.border}`, background: t.chipBg,
                          color: t.text, fontSize: 11, fontWeight: 500, cursor: 'pointer',
                          fontFamily: 'Outfit, sans-serif',
                        }}
                      >
                        <Icon.Plus c={t.muted} />{v.label}
                      </button>
                    ))}
                  </div>
                )}
                {bodyEditor && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                    <button
                      type="button"
                      title={tr('settings.email.toolbar.bold')}
                      aria-label={tr('settings.email.toolbar.bold')}
                      aria-pressed={bodyEditor.isActive('bold')}
                      onClick={() => bodyEditor.chain().focus().toggleBold().run()}
                      style={toolbarButtonStyle(t, bodyEditor.isActive('bold'))}
                    >
                      B
                    </button>
                    <button
                      type="button"
                      title={tr('settings.email.toolbar.italic')}
                      aria-label={tr('settings.email.toolbar.italic')}
                      aria-pressed={bodyEditor.isActive('italic')}
                      onClick={() => bodyEditor.chain().focus().toggleItalic().run()}
                      style={{ ...toolbarButtonStyle(t, bodyEditor.isActive('italic')), fontStyle: 'italic' }}
                    >
                      i
                    </button>
                    <button
                      type="button"
                      title={tr('settings.email.toolbar.bullet_list')}
                      aria-label={tr('settings.email.toolbar.bullet_list')}
                      aria-pressed={bodyEditor.isActive('bulletList')}
                      onClick={() => bodyEditor.chain().focus().toggleBulletList().run()}
                      style={toolbarButtonStyle(t, bodyEditor.isActive('bulletList'))}
                    >
                      {tr('settings.email.toolbar.bullet_list_btn')}
                    </button>
                    <button
                      type="button"
                      title={tr('settings.email.toolbar.ordered_list')}
                      aria-label={tr('settings.email.toolbar.ordered_list')}
                      aria-pressed={bodyEditor.isActive('orderedList')}
                      onClick={() => bodyEditor.chain().focus().toggleOrderedList().run()}
                      style={toolbarButtonStyle(t, bodyEditor.isActive('orderedList'))}
                    >
                      {tr('settings.email.toolbar.ordered_list_btn')}
                    </button>
                    <button
                      type="button"
                      title={tr('settings.email.toolbar.link_title')}
                      aria-label={tr('settings.email.toolbar.link')}
                      aria-pressed={bodyEditor.isActive('link')}
                      onClick={() => {
                        if (bodyEditor.isActive('link')) {
                          bodyEditor.chain().focus().unsetLink().run();
                          return;
                        }
                        const url = window.prompt(`${tr('settings.email.toolbar.link_prompt_before')} {{setcard_link}} ${tr('settings.email.toolbar.link_prompt_after')}`);
                        if (url) bodyEditor.chain().focus().setLink({ href: url }).run();
                      }}
                      style={toolbarButtonStyle(t, bodyEditor.isActive('link'))}
                    >
                      {tr('settings.email.toolbar.link')}
                    </button>
                  </div>
                )}
                <div style={editorWrapperStyle(t)}>
                  <EditorContent editor={bodyEditor} />
                </div>
              </Field>

              {/* Anhänge */}
              <Field label={tr('settings.email.field.attachments')}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: draft.attachments.length ? 8 : 0 }}>
                  {draft.attachments.map(a => (
                    <span
                      key={a.id}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '4px 6px 4px 10px', borderRadius: 9999,
                        border: `1px solid ${t.border}`, background: t.cardMuted,
                        color: t.text, fontSize: 11, maxWidth: 220,
                      }}
                    >
                      <Icon.Paperclip c={t.muted} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                      <button
                        type="button"
                        title={tr('settings.email.remove_attachment')}
                        aria-label={tr('settings.email.remove_attachment')}
                        onClick={() => removeAttachment(a.id)}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'inline-flex' }}
                      >
                        <Icon.Close c={t.muted} size={13} />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={onPickFile}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  dark={dark}
                  icon={<Icon.Paperclip />}
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? tr('settings.email.uploading') : tr('settings.email.upload_attachment')}
                </Button>
              </Field>

              {formError && <div style={{ fontSize: 12, color: '#f04438' }}>{formError}</div>}
              {savedNote && <div style={{ fontSize: 12, color: '#027a48' }}>{tr('settings.email.saved')}</div>}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button variant="primary" dark={dark} disabled={saving} onClick={save}>
                  {saving ? tr('settings.email.saving') : tr('settings.email.save')}
                </Button>
                <Button variant="ghost" dark={dark} onClick={closeEditor}>
                  {tr('common.action.cancel')}
                </Button>
              </div>

              {/* Test-Versand */}
              <div style={{ marginTop: 6, padding: 14, background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: t.heading, marginBottom: 8 }}>
                  {tr('settings.email.test.heading')}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <Field label={tr('settings.email.test.recipient')}>
                      <Input
                        type="email"
                        value={testTo}
                        onChange={e => setTestTo(e.target.value)}
                        placeholder={tr('settings.email.test.recipient_placeholder')}
                        dark={dark}
                      />
                    </Field>
                  </div>
                  <Button variant="secondary" dark={dark} disabled={sending} icon={<Icon.Mail />} onClick={sendTest}>
                    {sending ? tr('settings.email.test.sending') : tr('settings.email.test.send')}
                  </Button>
                </div>
                {testMsg && (
                  <div style={{ fontSize: 12, marginTop: 8, color: testMsg.kind === 'ok' ? '#027a48' : '#f04438' }}>
                    {testMsg.text}
                  </div>
                )}
              </div>
            </div>

            {/* Rechte Spalte: Live-Vorschau */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon.Eye c={t.muted} />
                <span style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>
                  {tr('settings.email.preview.heading')}
                </span>
                {previewLoading && <span style={{ fontSize: 11, color: t.muted }}>{tr('settings.email.preview.loading')}</span>}
              </div>
              <div style={{
                border: `1px solid ${t.border}`, borderRadius: 10, overflow: 'hidden',
                background: '#ffffff', minHeight: 320,
              }}>
                {previewHtml ? (
                  <iframe
                    title={tr('settings.email.preview.heading')}
                    srcDoc={sanitizePreview(previewHtml)}
                    sandbox=""
                    style={{ width: '100%', height: 420, border: 'none', background: '#fff' }}
                  />
                ) : (
                  <div style={{ padding: 20, fontSize: 12, color: '#98a2b3' }}>
                    {tr('settings.email.preview.empty')}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: t.muted, lineHeight: 1.5 }}>
                {tr('settings.email.preview.note')}
              </div>
            </div>
          </div>
        </Card>
        </div>
      )}
    </div>
  );
}

export default EmailTemplatesSettings;
